import React from 'react';
import { Checkbox, IconChevronDown, IconChevronUp } from '@zillow/constellation';
import {
  StyledExpandedContentContainer,
  StyledIconContainer,
  StyledTableBodyRow,
  StyledTableColumn,
} from './StyledComponents';
import type { ColumnConfig } from './types';
import type { AdvancedTableStyles, ExtendedRowData } from './AdvancedTable';
import { formatColWidth } from './utils';

interface TableMFHRowProps<T> {
  canExpandCurrentRow: boolean;
  canFilter: boolean;
  canSelectRow: boolean;
  columnConfig: Array<ColumnConfig<T>>;
  columnDivisions: number;
  data: ExtendedRowData<T>;
  edgePadding: number;
  ExpandedComponent?: (data: T) => JSX.Element;
  hasExpandableRows: boolean;
  isExpanded?: boolean;
  isSelected?: boolean;
  onToggleExpanded?: () => void;
  onToggleSelected?: () => void;
  rowSelectionDisabled: (data: T) => void;
  selectionDisabled: boolean;
  tableStyle: AdvancedTableStyles;
  rowNum: number;
  minWeightWidth: number;
  useFixedWidth: boolean;
  fixedWidth: number;
  topOffset?: number;
  absoluteRowNumber?: number;
  rowPaddingY?: number;
  currentRowHeight?: number;
}

const TableMFHRow = <T,>({
  canExpandCurrentRow = false,
  canFilter = false,
  canSelectRow = false,
  columnConfig = [],
  columnDivisions,
  data,
  edgePadding,
  hasExpandableRows,
  isExpanded = false,
  isSelected = false,
  onToggleSelected = () => {},
  onToggleExpanded = () => {},
  ExpandedComponent = () => <></>,
  selectionDisabled,
  rowSelectionDisabled = () => {},
  tableStyle,
  rowNum,
  minWeightWidth,
  useFixedWidth,
  topOffset,
  rowPaddingY,
  currentRowHeight,
}: TableMFHRowProps<T>) => {
  const formatValue = (col: ColumnConfig<T>) => {
    if (col.renderer) {
      return col.renderer(data);
    }
    if (col.dataKey) {
      return data[col.dataKey];
    }
    return '-';
  };

  const showEmptyColumn = (hasExpandableRows || canFilter) && !canExpandCurrentRow;

  return (
    <React.Fragment>
      <div
        style={{
          height: currentRowHeight ? currentRowHeight : undefined,
          top: topOffset ? topOffset : undefined,
          position: topOffset ? 'absolute' : undefined,
          width: '100%',
        }}
      >
        <StyledTableBodyRow
          width={useFixedWidth ? minWeightWidth : undefined}
          display="flex"
          alignItems="center"
          justifyContent="center"
          paddingLeft={edgePadding}
          paddingRight={edgePadding}
          isExpanded={isExpanded}
          isSelected={isSelected}
          data-test="row"
          tableStyle={tableStyle}
          rowNum={rowNum}
          paddingY={rowPaddingY}
        >
          {canSelectRow && (
            <StyledTableColumn width={formatColWidth(1, columnDivisions)}>
              <StyledIconContainer display="flex" justifyContent="center">
                <Checkbox
                  checked={isSelected}
                  onChange={onToggleSelected}
                  // @ts-ignore
                  disabled={selectionDisabled || rowSelectionDisabled(data)}
                />
              </StyledIconContainer>
            </StyledTableColumn>
          )}
          {columnConfig.map((col, i) => (
            <StyledTableColumn
              key={i}
              width={formatColWidth(col.width, columnDivisions)}
              align={col.align}
              className={col.className}
            >
              <>{formatValue(col)}</>
            </StyledTableColumn>
          ))}
          {canExpandCurrentRow && (
            <StyledTableColumn width={formatColWidth(1, columnDivisions)}>
              <StyledIconContainer display="flex" justifyContent="center" onClick={onToggleExpanded}>
                {isExpanded ? (
                  <IconChevronUp className="TableMFH-expand-icon" size="xs" />
                ) : (
                  <IconChevronDown className="TableMFH-expand-icon" size="xs" />
                )}
              </StyledIconContainer>
            </StyledTableColumn>
          )}
          {showEmptyColumn && <StyledTableColumn width={formatColWidth(1, columnDivisions)} />}
        </StyledTableBodyRow>
        {canExpandCurrentRow && isExpanded ? (
          <StyledExpandedContentContainer>{ExpandedComponent(data)}</StyledExpandedContentContainer>
        ) : null}
      </div>
    </React.Fragment>
  );
};

export default TableMFHRow;
